import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getHSEObservationsList,
  saveHSEObservationRow,
  deleteHSEObservationRow,
  getActivitiesList,
  type HSEObservationRow,
  type ActivityRow,
} from '@/services/db/dsrEntry';
import PhotoUploader from '@/components/dsr/PhotoUploader';
import CreateActionItemButton from './CreateActionItemButton';
import { FieldLabel, FieldError, ChipGroup, timeHHMMSS } from './_shared';

const OBSERVATION_CATEGORIES = [
  { value: 'safe_behavior', label: 'Safe Behavior' },
  { value: 'good_housekeeping', label: 'Good Housekeeping' },
  { value: 'proper_ppe_use', label: 'Proper PPE Use' },
  { value: 'hazard_identification', label: 'Hazard Identification' },
  { value: 'team_communication', label: 'Team Communication' },
  { value: 'procedure_compliance', label: 'Procedure Compliance' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'other', label: 'Other' },
] as const;

const entrySchema = z.object({
  description: z.string().min(1, 'Description is required').max(500),
  observation_category: z.string().min(1, 'Category is required'),
  observation_time: z.string().optional(),
  location_detail: z.string().max(200).optional(),
  associated_crew: z.string().optional(),
  activity_entry_id: z.string().optional(),
  activity_free_text: z.string().optional(),
  comments: z.string().max(300).optional(),
});

type EntryFormValues = z.infer<typeof entrySchema>;

function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const EMPTY: EntryFormValues = {
  description: '',
  observation_category: '',
  observation_time: nowHHMM(),
  location_detail: '',
  associated_crew: '',
  activity_entry_id: '',
  activity_free_text: '',
  comments: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function GoodCatchForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: HSEObservationRow }>(
    'closed',
  );

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_hse_observations', dsrId],
    queryFn: () => getHSEObservationsList(dsrId),
    staleTime: 30 * 1000,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['entry_activities', dsrId],
    queryFn: () => getActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_hse_observations', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this observation?')) return;
    try {
      await deleteHSEObservationRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_hse_observations', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete observation');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Good Catch</h2>
        <p className="text-xs text-muted-foreground">
          Recognise positive HSE behaviours, hazard identification, and other good catches.
        </p>
      </div>

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No good catches recorded yet. Add one below.
          </CardContent>
        </Card>
      )}

      {entries.map((entry) => (
        <ObservationCard
          key={entry.id}
          entry={entry}
          dsrId={dsrId}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <ObservationEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          activities={activities}
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {!isReadOnly && mode === 'closed' && (
        <Button
          variant="outline"
          className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
          onClick={() => setMode('adding')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add Good Catch
        </Button>
      )}
    </div>
  );
}

function categoryLabel(value: string): string {
  return OBSERVATION_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

function ObservationCard({
  entry,
  dsrId,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: HSEObservationRow;
  dsrId: string;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-center justify-between gap-3">
          <Badge className="bg-mozaic-teal text-white">
            {categoryLabel(entry.observation_category).toUpperCase()}
          </Badge>
          {entry.observation_time && (
            <span className="text-xs text-muted-foreground">
              {timeHHMMSS(entry.observation_time)}
            </span>
          )}
        </div>
        <p className="font-semibold text-mozaic-navy">{entry.description}</p>
        {entry.location_detail && (
          <p className="mt-1 text-xs text-muted-foreground">{entry.location_detail}</p>
        )}
        <PhotoUploader
          entityType="positive_observation"
          entityId={entry.id}
          dsrId={dsrId}
          readOnly={isReadOnly}
        />
        {!isReadOnly && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
            <div className="ml-auto">
              <CreateActionItemButton
                dsrId={dsrId}
                sourceSection="good_catch"
                defaultDescription={`Good catch follow-up: ${
                  entry.description.length > 100
                    ? entry.description.slice(0, 100).trimEnd() + '…'
                    : entry.description
                }`}
                defaultPriority="medium"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ObservationEntryForm({
  dsrId,
  tenantId,
  activities,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  activities: ActivityRow[];
  editing?: HSEObservationRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const hasActivities = activities.length > 0;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          description: editing.description,
          observation_category: editing.observation_category,
          observation_time: timeHHMMSS(editing.observation_time),
          location_detail: editing.location_detail ?? '',
          associated_crew: editing.associated_crew ?? '',
          activity_entry_id: editing.activity_entry_id ?? '',
          activity_free_text: editing.activity_free_text ?? '',
          comments: editing.comments ?? '',
        }
      : EMPTY,
  });

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveHSEObservationRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        description: values.description,
        observationCategory: values.observation_category,
        observationTime: values.observation_time || null,
        locationDetail: values.location_detail || null,
        associatedCrew: values.associated_crew || null,
        activityEntryId:
          hasActivities && values.activity_entry_id ? values.activity_entry_id : null,
        activityFreeText:
          !hasActivities && values.activity_free_text ? values.activity_free_text : null,
        comments: values.comments || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save observation');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit Good Catch' : 'New Good Catch'}</h3>

        <div className="space-y-2">
          <FieldLabel required>Description</FieldLabel>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                {...field}
                rows={3}
                maxLength={500}
                placeholder="What did you observe?"
              />
            )}
          />
          <FieldError message={errors.description?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Category</FieldLabel>
          <Controller
            control={control}
            name="observation_category"
            render={({ field }) => (
              <ChipGroup
                options={OBSERVATION_CATEGORIES}
                value={field.value}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.observation_category?.message} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel>Time</FieldLabel>
            <Controller
              control={control}
              name="observation_time"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} type="time" />
              )}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>Site Location</FieldLabel>
            <Controller
              control={control}
              name="location_detail"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  maxLength={200}
                  placeholder="Where on the site?"
                />
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel>Associated Crew</FieldLabel>
          <Controller
            control={control}
            name="associated_crew"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                placeholder="Crew or contractor involved"
              />
            )}
          />
        </div>

        {hasActivities ? (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_entry_id"
              render={({ field }) => (
                <ChipGroup
                  options={[
                    { value: '', label: 'None' },
                    ...activities.map((a) => ({
                      value: a.id,
                      label:
                        a.description.length > 30
                          ? a.description.slice(0, 30) + '…'
                          : a.description,
                    })),
                  ]}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  wrap
                />
              )}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_free_text"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  placeholder="Describe the associated activity"
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel>Comments</FieldLabel>
          <Controller
            control={control}
            name="comments"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                maxLength={300}
                placeholder="Additional notes"
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Good Catch'
            ) : (
              'Save Good Catch'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
